import { CalendarApi, EventInput } from '@fullcalendar/core';
import FullCalendar from '@fullcalendar/react';
import { addDays, endOfDay, startOfDay } from 'date-fns';
import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import { getTasksEventsForDateRange } from 'shared/lib/full-calendar/get-tasks-events-for-date-range';
import { ID } from 'shared/types/id';
import { Task } from 'shared/types/task';
import { TaskType } from 'shared/types/task-base';
import { Timestamp } from 'shared/types/timestamp';
import { WeekDays } from 'shared/types/week-days';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';

export type UseFirestoreCalendarSyncOptions = {
  rangeReferenceDate: Timestamp;
  dayRange: number;
  hideCompleted?: boolean;
  hideHabits?: boolean;
};

export const useFirestoreCalendarSync = (
  calendarRef: RefObject<FullCalendar | null>,
  tasks: Task[],
  {
    rangeReferenceDate,
    dayRange,
    hideCompleted,
    hideHabits,
  }: UseFirestoreCalendarSyncOptions,
) => {
  const user = useUser();
  const today = useToday();
  const weekStartsOn = getWeekStartsOn(user);

  // This ref acts as the "Truth" of what is currently on the calendar.
  const renderedTasksRef = useRef<Map<ID, Task>>(new Map());
  // This ref holds the latest set of tasks we received from the prop.
  const latestTasksRef = useRef<Task[]>(tasks);
  // This ref controls the pause state.
  const isPausedRef = useRef(false);

  // Buffer just tracks if we *need* to sync when resuming.
  // Since we have the full array in latestTasksRef, we don't need a queue of "patches".
  // We just need a flag saying "Data is dirty".
  const needsSyncRef = useRef(false);

  // Helper to remove events for a specific task
  const removeTaskEvents = (calendarApi: CalendarApi, taskId: ID) => {
    const events = calendarApi.getEvents();
    events.forEach((event) => {
      if (event.extendedProps?.taskId === taskId) {
        event.remove();
      }
    });
  };

  // Helper to generate and add events for a task
  const addTaskEvents = useCallback(
    (calendarApi: CalendarApi, task: Task) => {
      // Client-side filtering
      // Note: if filteredTasks is passed, this is redundant but safe.
      if (hideHabits && task.type === TaskType.Habit) {
        return;
      }

      const events = getTasksEventsForDateRange([task], {
        startDate: startOfDay(rangeReferenceDate),
        endDate: endOfDay(addDays(rangeReferenceDate, dayRange - 1)),
        today,
        weekStartsOn: weekStartsOn as WeekDays,
        hideCompleted,
      });

      events.forEach((event: EventInput) => {
        calendarApi.addEvent(event);
      });
    },
    [
      dayRange,
      hideCompleted,
      hideHabits,
      rangeReferenceDate,
      today,
      weekStartsOn,
    ],
  );

  const updateTaskEvents = useCallback(
    (calendarApi: CalendarApi, task: Task) => {
      const newEventsRaw = getTasksEventsForDateRange([task], {
        startDate: startOfDay(rangeReferenceDate),
        endDate: endOfDay(addDays(rangeReferenceDate, dayRange - 1)),
        today,
        weekStartsOn: weekStartsOn as WeekDays,
        hideCompleted,
      });

      // Mapping new events by ID
      const newEventIds = new Set(newEventsRaw.map(({ id }) => id));

      // Remove events that are no longer present
      const currentEvents = calendarApi
        .getEvents()
        .filter((e) => e.extendedProps?.taskId === task.id);

      currentEvents.forEach((existingEvent) => {
        if (!newEventIds.has(existingEvent.id)) {
          existingEvent.remove();
        }
      });

      // Update or Add new events
      newEventsRaw.forEach((eventData) => {
        const existingEvent = eventData.id
          ? calendarApi.getEventById(eventData.id)
          : null;
        if (existingEvent) {
          // Update
          if (eventData.start && eventData.end) {
            existingEvent.setDates(eventData.start, eventData.end);
          }
          existingEvent.setProp('title', eventData.title);
          // setProp cannot be used for allDay, use setAllDay instead
          existingEvent.setAllDay(!!eventData.allDay);
          // Update extended props
          if (eventData.extendedProps) {
            Object.entries(eventData.extendedProps).forEach(([key, value]) => {
              existingEvent.setExtendedProp(key, value);
            });
          }
          return;
        }
        // Add
        calendarApi.addEvent(eventData);
      });
    },
    [dayRange, hideCompleted, rangeReferenceDate, today, weekStartsOn],
  );

  // Deep comparison to filter redundancy
  const hasChanged = useCallback((prev: Task, next: Task) => {
    const { updatedAt: _u1, createdAt: _c1, frozenAt: _f1, ...p } = prev;
    const { updatedAt: _u2, createdAt: _c2, frozenAt: _f2, ...n } = next;
    return JSON.stringify(p) !== JSON.stringify(n);
  }, []);

  const sync = useCallback(() => {
    if (!calendarRef.current) {
      return;
    }
    const calendarApi = calendarRef.current.getApi();

    if (isPausedRef.current) {
      needsSyncRef.current = true;
      return;
    }

    const nextTasks = latestTasksRef.current;
    const nextTasksMap = new Map(nextTasks.map((t) => [t.id, t]));
    const currentTasksMap = renderedTasksRef.current;

    const performSync = () => {
      // 1. Detect Removed
      currentTasksMap.forEach((_, id) => {
        if (!nextTasksMap.has(id)) {
          removeTaskEvents(calendarApi, id);
          currentTasksMap.delete(id);
        }
      });

      // 2. Detect Added / Modified
      nextTasks.forEach((task) => {
        if (!currentTasksMap.has(task.id)) {
          // Added
          addTaskEvents(calendarApi, task);
          currentTasksMap.set(task.id, task);
          return;
        }
        // Modified
        const prevTask = currentTasksMap.get(task.id)!;
        if (hasChanged(prevTask, task)) {
          updateTaskEvents(calendarApi, task);
          currentTasksMap.set(task.id, task);
        }
      });
      needsSyncRef.current = false;
    };

    void setTimeout(() => {
      if ((calendarApi as any).batchRendering) {
        (calendarApi as any).batchRendering(performSync);
        return;
      }

      performSync();
    }, 0);
  }, [addTaskEvents, calendarRef, hasChanged, updateTaskEvents]);

  const pause = useCallback(() => {
    isPausedRef.current = true;
  }, []);

  const resume = useCallback(() => {
    isPausedRef.current = false;
    if (needsSyncRef.current) {
      sync();
    }
  }, [sync]);

  // Update latest tasks whenever prop changes
  useEffect(() => {
    latestTasksRef.current = tasks;
  }, [tasks]);

  // Trigger sync when tasks change
  useEffect(() => {
    sync();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- we want to sync when tasks (ref) changes, but here we depend on the prop
  }, [tasks]);

  // Re-render all events when view options change (range, filters)
  useEffect(() => {
    if (!calendarRef.current) {
      return;
    }
    const calendarApi = calendarRef.current.getApi();

    const render = () => {
      // Remove all task events
      const events = calendarApi.getEvents();
      events.forEach((event) => {
        if (event.extendedProps?.taskId) {
          event.remove();
        }
      });
      // Clear cache
      renderedTasksRef.current.clear();

      // Re-add all tasks from latest ref
      latestTasksRef.current.forEach((task) => {
        addTaskEvents(calendarApi, task);
        renderedTasksRef.current.set(task.id, task);
      });
    };

    setTimeout(() => {
      if ((calendarApi as any).batchRendering) {
        (calendarApi as any).batchRendering(render);
        return;
      }

      render();
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-render all events when view options change
  }, [rangeReferenceDate, dayRange, hideCompleted, hideHabits, user, today]);

  return { pause, resume };
};
